// @flow

import * as React from 'react';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectCurrentId } from '@gandi/redux-sharing';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { PreFetchArgs, PreFetchReturn, FullState } from '../../../types';

import AnonymousDialog from './AnonymousDialog';
import AuthenticatedDialog from './AuthenticatedDialog';
import PreferencesButton from './PreferencesButton';
import type { DialogState } from './types';

type Props = { dialog: DialogState };

export default function PreferencesWidget({
  dialog,
}: Props): React.MixedElement {
  const isAuthencatedUser = useAppSelector(selectIsAuthenticatedUser);

  return (
    <>
      <PreferencesButton onClick={dialog.show} />
      {isAuthencatedUser ? (
        <AuthenticatedDialog dialogState={dialog} />
      ) : (
        <AnonymousDialog dialogState={dialog} displayTaxesSwitch />
      )}
    </>
  );
}

PreferencesWidget.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return selectIsAuthenticatedUser(args.getState())
    ? AuthenticatedDialog.preFetch(args)
    : AnonymousDialog.preFetch(args);
};

PreferencesWidget.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  const { req, res, getState } = args;
  const authenticated = selectIsAuthenticatedUser(getState());

  const { org, ...newQuery } = req.query;
  if (authenticated && org) {
    const sharingId = selectCurrentId(getState());
    const redirectTo = req.originalUrl.replace(sharingId, org).split('?')[0];

    const querystring = Object.keys(newQuery).reduce(
      (acc, param) =>
        `${acc ? `${acc}&` : '?'}${param}=${newQuery[param] || ''}`,
      '',
    );

    res.redirect(redirectTo + querystring);
    return Promise.reject(new Error('redirect to ?org sharing_id'));
  }

  return Promise.resolve();
};
